/**
 * Backup section — generates a zip archive of all user data + study PDFs.
 */

import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { DownloadIcon, LoadingSpinner } from '@components/icons';
import { generateStudyPdf } from '@/services/study-pdf';
import { getBookInfo, formatShortReference, type Study } from '@skjv/common';
import { getStorageRouter } from '@skjv/storage';

export const BackupSection = observer(function BackupSection() {
  const [isBackingUp, setIsBackingUp] = useState(false);

  const buildBackupZip = async (): Promise<{ blob: Blob; filename: string }> => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const date = new Date().toISOString().split('T')[0];

    zip.file(
      'preferences.json',
      JSON.stringify(
        {
          themeMode: bibleState.themeMode,
          fontScale: bibleState.fontScale,
          colorTheme: bibleState.colorTheme,
        },
        null,
        2,
      ),
    );

    const collections = [
      { key: 'study-groups', data: bibleState.studyGroups },
      { key: 'bookmarks', data: bibleState.bookmarks },
      { key: 'notes', data: bibleState.notes },
      { key: 'prayer-items', data: bibleState.prayerItems },
    ];
    for (const { key, data } of collections) {
      if (data.length > 0) zip.file(`${key}.json`, JSON.stringify(data, null, 2));
    }

    await addStudiesToZip(zip, bibleState.studies);

    const blob = await zip.generateAsync({ type: 'blob' });
    return { blob, filename: `skjv-backup-${date}.zip` };
  };

  const handleBackup = async () => {
    setIsBackingUp(true);
    try {
      const { blob, filename } = await buildBackupZip();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      void getStorageRouter().putBlob('backups', filename, blob);
    } catch (error) {
      console.error('Backup failed:', error);
      bibleState.showToast('Backup failed. Please try again.', 'error');
    } finally {
      setIsBackingUp(false);
    }
  };

  const studyCount = bibleState.studies.length;
  const groupCount = bibleState.studyGroups.length;
  const bookmarkCount = bibleState.bookmarks.length;
  const noteCount = bibleState.notes.length;
  const prayerCount = bibleState.prayerItems.length;

  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-stone-800 dark:text-stone-200">Backup</h2>
      <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
        Download all your data as a zip archive
      </p>

      {/* Backup summary */}
      <div className="mb-3 rounded-xl bg-stone-100/50 p-3 dark:bg-stone-800/30">
        <div className="grid grid-cols-2 gap-2 text-xs text-stone-600 dark:text-stone-400">
          <div>{studyCount} {studyCount === 1 ? 'study' : 'studies'}</div>
          <div>{groupCount} {groupCount === 1 ? 'group' : 'groups'}</div>
          <div>{bookmarkCount} {bookmarkCount === 1 ? 'bookmark' : 'bookmarks'}</div>
          <div>{noteCount} {noteCount === 1 ? 'note' : 'notes'}</div>
          <div>{prayerCount} prayer {prayerCount === 1 ? 'item' : 'items'}</div>
          <div>+ preferences</div>
        </div>
      </div>

      <button
        onClick={handleBackup}
        disabled={isBackingUp}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 font-medium text-white transition-colors hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isBackingUp ? (
          <>
            <LoadingSpinner className="h-5 w-5 animate-spin" />
            Creating backup...
          </>
        ) : (
          <>
            <DownloadIcon className="h-5 w-5" />
            Download Backup
          </>
        )}
      </button>
    </section>
  );
});

/** Add studies with PDFs organized by book into the zip */
async function addStudiesToZip(zip: import('jszip'), studies: Study[]) {
  if (studies.length === 0) return;

  const studiesFolder = zip.folder('studies');
  const studiesByBook = new Map<string, Study[]>();

  for (const study of studies) {
    const bookInfo = getBookInfo(
      study.startingLocation.testamentIndex,
      study.startingLocation.bookIndex,
    );
    const bookAbbrev = bookInfo?.abbrev || 'Unknown';
    if (!studiesByBook.has(bookAbbrev)) studiesByBook.set(bookAbbrev, []);
    studiesByBook.get(bookAbbrev)!.push(study);
  }

  for (const [bookAbbrev, bookStudies] of studiesByBook) {
    const bookFolder = studiesFolder?.folder(bookAbbrev);
    for (const study of bookStudies) {
      const startRef = formatShortReference(study.startingLocation);
      let filename: string;
      if (study.endingLocation) {
        const endRef = formatShortReference(study.endingLocation);
        const endVerse = endRef.split(' ').slice(1).join(' ');
        filename = `${startRef}- ${endVerse} - ${study.name}`;
      } else {
        filename = `${startRef} - ${study.name}`;
      }
      const safeFilename = filename.replace(/[<>:"/\\|?*]/g, '');

      if (bookFolder) {
        bookFolder.file(`${safeFilename}.json`, JSON.stringify(study, null, 2));
        try {
          const pdfBlob = await generateStudyPdf(study);
          bookFolder.file(`${safeFilename}.pdf`, pdfBlob);
        } catch (err) {
          console.warn(`Failed to generate PDF for study: ${study.name}`, err);
        }
      }
    }
  }
}
